// src/components/RegistrarContratos.jsx
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const isValidObjectId = (v) => /^[0-9a-fA-F]{24}$/.test(String(v || ""));

const RegistrarContratos = () => {
  const { id: instructorId, idcontrato } = useParams();
  const navigate = useNavigate();

  const isEdit = useMemo(() => Boolean(idcontrato), [idcontrato]);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("tab1");

  const [contrato, setContrato] = useState("");
  const [rp, setRp] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [valorContrato, setValorContrato] = useState("");
  const [numeroCuenta, setNumeroCuenta] = useState("");
  const [tipoCuenta, setTipoCuenta] = useState("");
  const [banco, setBanco] = useState("");
  const [municipioSede, setMunicipioSede] = useState("");
  const [estado, setEstado] = useState("");

  const [regionales, setRegionales] = useState([]);
  const [regional, setRegional] = useState("");
  const [centros, setCentros] = useState([]);
  const [centroFormacion, setCentroFormacion] = useState("");

  const [objetoContrato, setObjetoContrato] = useState("");
  const [valorFormaPago, setValorFormaPago] = useState("");

  const [supervisor, setSupervisor] = useState({
    documento: "",
    nombre: "",
    cargo: "",
  });

  const [subdirector, setSubdirector] = useState({
    documento: "",
    nombre: "",
    cargo: "",
  });

  const cleanData = useCallback(() => {
    setContrato("");
    setRp("");
    setFechaInicio("");
    setFechaFin("");
    setValorContrato("");
    setNumeroCuenta("");
    setTipoCuenta("");
    setBanco("");
    setMunicipioSede("");
    setEstado("");
    setRegional("");
    setCentroFormacion("");
    setCentros([]);
    setObjetoContrato("");
    setValorFormaPago("");
    setSupervisor({ documento: "", nombre: "", cargo: "" });
    setSubdirector({ documento: "", nombre: "", cargo: "" });
    setActiveTab("tab1");
  }, []);

  // -------------------- Cargar contrato si edita --------------------
  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`/api/contratos/listid/${idcontrato}`);
        const c = data?.contrato;

        setContrato(c?.contrato || "");
        setRp(c?.rp || "");
        setFechaInicio(c?.fechaInicio?.substring(0, 10) || "");
        setFechaFin(c?.fechaFin?.substring(0, 10) || "");
        setValorContrato(c?.valorContrato || "");
        setNumeroCuenta(c?.numeroCuenta || "");
        setTipoCuenta(c?.tipoCuenta || "");
        setBanco(c?.banco || "");
        setMunicipioSede(c?.municipioSede || "");
        setEstado(c?.estado || "");

        setRegional(c?.regional || "");
        setCentroFormacion(c?.centroFormacion || "");

        setObjetoContrato(c?.objetoContrato || "");
        setValorFormaPago(c?.valorFormaPago || "");

        setSupervisor(c?.supervisor || { documento: "", nombre: "", cargo: "" });
        setSubdirector(c?.subdirector || { documento: "", nombre: "", cargo: "" });
      } catch (error) {
        console.error("Error al cargar el contrato:", error);
        Swal.fire({
          icon: "error",
          title: "Error al cargar contrato",
          text: error.response?.data?.message || "Error inesperado al cargar datos.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (isEdit && idcontrato) getData();
  }, [idcontrato, isEdit]);

  // -------------------- Regionales --------------------
  useEffect(() => {
    const fetchRegionales = async () => {
      try {
        const res = await axios.get("/api/regionales/listall");
        if (res.data.ok) {
          const regionalesOrdenadas = (res.data.regionales || []).sort((a, b) =>
            a.nombreRegional.localeCompare(b.nombreRegional)
          );
          setRegionales(regionalesOrdenadas);
        }
      } catch (error) {
        console.error("Error al obtener las regionales:", error);
      }
    };

    fetchRegionales();
  }, []);

  // -------------------- Centros por regional --------------------
  useEffect(() => {
    const fetchCentros = async () => {
      if (!regional) {
        setCentros([]);
        setCentroFormacion("");
        return;
      }

      try {
        const res = await axios.get(
          `/api/centrosdeformacion/listxregional/${regional}`
        );
        if (res.data.ok) {
          setCentros(res.data?.regional?.centros || []);
        }
      } catch (error) {
        console.error("Error al obtener centros:", error);
      }
    };

    fetchCentros();
  }, [regional]);

  // -------------------- Guardar / Actualizar --------------------
  const saveContrato = async () => {
    try {
      // ✅ Validación fuerte del instructorId
      if (!isValidObjectId(instructorId)) {
        return Swal.fire({
          icon: "warning",
          title: "Falta instructor",
          text: "No llegó un id válido del instructor en la URL.",
        });
      }

      const newContrato = {
        contrato,
        rp,
        fechaInicio,
        fechaFin,
        valorContrato,
        numeroCuenta,
        tipoCuenta,
        banco,
        municipioSede,
        estado,
        regional,
        centroFormacion,
        supervisor,
        subdirector,
        objetoContrato,
        valorFormaPago,

        // ✅ CAMBIO CLAVE: el backend debe leer esto
        instructorObjectId: instructorId,
      };

      setLoading(true);
      await axios.post("/api/contratos/add", newContrato);

      await Swal.fire({
        icon: "success",
        title: "¡Contrato registrado con éxito!",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate("/instructor/");
    } catch (error) {
      console.error("Error en saveContrato:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al guardar el contrato.",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateContrato = async () => {
    try {
      const contratoActualizado = {
        contrato,
        rp,
        fechaInicio,
        fechaFin,
        valorContrato,
        numeroCuenta,
        tipoCuenta,
        banco,
        municipioSede,
        estado,
        regional,
        centroFormacion,
        supervisor,
        subdirector,
        objetoContrato,
        valorFormaPago,
      };

      setLoading(true);
      await axios.put(`/api/contratos/update/${idcontrato}`, contratoActualizado);

      await Swal.fire({
        icon: "success",
        title: "¡Contrato actualizado con éxito!",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate(-1);
    } catch (error) {
      console.error("Error al actualizar contrato:", error);
      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text: error.response?.data?.message || "Error inesperado",
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();
    isEdit ? await updateContrato() : await saveContrato();
  };

  const disabledAll = loading;

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Contrato" : "Registrar Contrato"}
                </h5>
                <small className="text-secondary">
                  Completa la información contractual, responsables y datos adicionales.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(-1)}
                  disabled={disabledAll}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={cleanData}
                  disabled={disabledAll}
                  title="Limpiar"
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            {/* Tabs */}
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "tab1" ? "active" : ""}`}
                  onClick={() => setActiveTab("tab1")}
                  disabled={disabledAll}
                >
                  <i className="fa-solid fa-file-contract me-2"></i>
                  Contrato
                </button>
              </li>

              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "tab2" ? "active" : ""}`}
                  onClick={() => setActiveTab("tab2")}
                  disabled={disabledAll}
                >
                  <i className="fa-solid fa-user-tie me-2"></i>
                  Supervisor / Subdirector
                </button>
              </li>

              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "tab3" ? "active" : ""}`}
                  onClick={() => setActiveTab("tab3")}
                  disabled={disabledAll}
                >
                  <i className="fa-solid fa-circle-info me-2"></i>
                  Adicional
                </button>
              </li>
            </ul>

            <form id="form-contrato" onSubmit={actions}>
              {/* TAB 1 */}
              {activeTab === "tab1" && (
                <div className="border rounded-4 p-3 p-md-4 mb-3">
                  <div className="fw-semibold mb-2">Información del Contrato</div>

                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-2">
                      <label className="form-label fw-semibold">Contrato</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: 001-2025"
                        value={contrato}
                        onChange={(e) => setContrato(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-semibold">RP</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: 001-2025"
                        value={rp}
                        onChange={(e) => setRp(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-semibold">Fecha Inicio</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-semibold">Fecha Fin</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Valor del Contrato ($)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Ej: 15000000"
                        value={valorContrato}
                        onChange={(e) => setValorContrato(e.target.value)}
                        required
                        min="0"
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Número de Cuenta</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: 1234567890"
                        value={numeroCuenta}
                        onChange={(e) => setNumeroCuenta(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Tipo de Cuenta</label>
                      <select
                        className="form-select"
                        value={tipoCuenta}
                        onChange={(e) => setTipoCuenta(e.target.value)}
                        required
                        disabled={disabledAll}
                      >
                        <option value="">Seleccione…</option>
                        <option value="Ahorros">Ahorros</option>
                        <option value="Corriente">Corriente</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Banco</label>
                      <select
                        className="form-select"
                        value={banco}
                        onChange={(e) => setBanco(e.target.value)}
                        required
                        disabled={disabledAll}
                      >
                        <option value="">Seleccione…</option>
                        <option value="Bancolombia">Bancolombia</option>
                        <option value="Banco de Bogotá">Banco de Bogotá</option>
                        <option value="Banco Davivienda">Banco Davivienda</option>
                        <option value="Banco de Occidente">Banco de Occidente</option>
                        <option value="Banco AV Villas">Banco AV Villas</option>
                        <option value="Banco Agrario">Banco Agrario</option>
                        <option value="Banco Popular">Banco Popular</option>
                        <option value="Banco Itaú">Banco Itaú</option>
                        <option value="Banco BBVA">Banco BBVA</option>
                        <option value="Banco Falabella">Banco Falabella</option>
                        <option value="Banco Pichincha">Banco Pichincha</option>
                        <option value="Banco Finandina">Banco Finandina</option>
                        <option value="Banco Caja Social">Banco Caja Social</option>
                        <option value="Banco GNB Sudameris">Banco GNB Sudameris</option>
                        <option value="Banco Coopcentral">Banco Coopcentral</option>
                        <option value="Banco Serfinanza">Banco Serfinanza</option>
                        <option value="Nequi">Nequi</option>
                        <option value="Daviplata">Daviplata</option>
                        <option value="Movii">Movii</option>
                        <option value="Lulo Bank">Lulo Bank</option>
                        <option value="Nu Colombia">Nu Colombia</option>
                        <option value="RappiPay">RappiPay</option>
                        <option value="Banco W">Banco W</option>
                        <option value="Banco Mundo Mujer">Banco Mundo Mujer</option>
                        <option value="Scotiabank Colpatria">Scotiabank Colpatria</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Municipio Sede</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: Medellín"
                        value={municipioSede}
                        onChange={(e) => setMunicipioSede(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Estado</label>
                      <select
                        className="form-select"
                        value={estado}
                        onChange={(e) => setEstado(e.target.value)}
                        required
                        disabled={disabledAll}
                      >
                        <option value="">Seleccione…</option>
                        <option value="Activo">Activo</option>
                        <option value="Finalizado">Finalizado</option>
                        <option value="Suspendido">Suspendido</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Regional</label>
                      <select
                        className="form-select"
                        value={regional}
                        onChange={(e) => setRegional(e.target.value)}
                        required
                        disabled={disabledAll}
                      >
                        <option value="">Seleccione…</option>
                        {regionales.map((reg) => (
                          <option key={reg._id} value={reg._id}>
                            {reg.nombreRegional}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">Centro de Formación</label>
                      <select
                        className="form-select"
                        value={centroFormacion}
                        onChange={(e) => setCentroFormacion(e.target.value)}
                        required
                        disabled={disabledAll || !regional}
                      >
                        <option value="">Seleccione…</option>
                        {centros.map((centro) => (
                          <option key={centro._id} value={centro._id}>
                            {centro.nombreCentro}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2 */}
              {activeTab === "tab2" && (
                <div className="border rounded-4 p-3 p-md-4 mb-3">
                  <div className="fw-semibold mb-2">Supervisor del Contrato</div>

                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Documento</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: 1234567890"
                        value={supervisor.documento}
                        onChange={(e) =>
                          setSupervisor({ ...supervisor, documento: e.target.value })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-5">
                      <label className="form-label fw-semibold">Nombre Completo</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: Juan Pérez"
                        value={supervisor.nombre}
                        onChange={(e) =>
                          setSupervisor({ ...supervisor, nombre: e.target.value })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Cargo</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: Coordinador Regional"
                        value={supervisor.cargo}
                        onChange={(e) =>
                          setSupervisor({ ...supervisor, cargo: e.target.value })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>
                  </div>

                  <hr className="my-4" />

                  <div className="fw-semibold mb-2">Subdirector(a) Encargado(a) (E)</div>

                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Documento</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: 9876543210"
                        value={subdirector.documento}
                        onChange={(e) =>
                          setSubdirector({ ...subdirector, documento: e.target.value })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-5">
                      <label className="form-label fw-semibold">Nombre Completo</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: María Gómez"
                        value={subdirector.nombre}
                        onChange={(e) =>
                          setSubdirector({ ...subdirector, nombre: e.target.value })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold">Cargo</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ej: Subdirectora Encargada"
                        value={subdirector.cargo}
                        onChange={(e) =>
                          setSubdirector({ ...subdirector, cargo: e.target.value })
                        }
                        required
                        disabled={disabledAll}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3 */}
              {activeTab === "tab3" && (
                <div className="border rounded-4 p-3 p-md-4 mb-3">
                  <div className="fw-semibold mb-2">Información Adicional</div>

                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-semibold">Objeto del Contrato</label>
                      <textarea
                        className="form-control"
                        placeholder="Descripción del objeto"
                        rows="4"
                        value={objetoContrato}
                        onChange={(e) => setObjetoContrato(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">Valor y Forma de Pago</label>
                      <textarea
                        className="form-control"
                        placeholder="Descripción del pago"
                        rows="4"
                        value={valorFormaPago}
                        onChange={(e) => setValorFormaPago(e.target.value)}
                        required
                        disabled={disabledAll}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Footer acciones */}
              <div className="d-flex flex-wrap gap-2 justify-content-end pt-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(-1)}
                  disabled={disabledAll}
                  title="Cancelar"
                >
                  <i className="fa-solid fa-xmark me-2"></i>
                  Cancelar
                </button>

                <button
                  type="submit"
                  form="form-contrato"
                  className="btn btn-dark"
                  disabled={disabledAll}
                  title={isEdit ? "Actualizar" : "Guardar"}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                    } me-2`}
                  ></i>
                  {isEdit ? "Actualizar" : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarContratos;
